/**
 * <subscription-selector>
 *
 * Single source of truth for the selling plan applied to its product form.
 * Wraps a <form is="product-form"> and owns the hidden `selling_plan` input that
 * ProductForm serializes via `new FormData(form)`.
 *
 * Selection sources:
 *   - PDP  : the Recharge subscription widget (read on submit — P2, this file)
 *   - cards: a custom subscribe/one-time toggle (P3) calling setSellingPlan()
 *   - subscription-only products are seeded server-side from Liquid (no source needed)
 *
 * Recharge bridge (P2): the Recharge "Subscription Widget V2" is a headless Lit element
 * (<recharge-subscription-widget>) rendered outside the form. It does not submit to cart
 * or inject inputs — it exposes live reactive properties (`purchaseOption`, `sellingPlanId`).
 * We use a PULL model: on submit, read the widget's current selection and write it into the
 * hidden input just before ProductForm serializes the form. Pull-on-submit can't miss an
 * update and needs no variant re-sync (the widget owns its own state).
 *
 * The submit listener is bound on this element (the wrapper) with `capture: true`, so it runs
 * during the capture phase — before the form's own (bubble-phase) ProductForm submit handler.
 */
if (!customElements.get('subscription-selector')) {
  customElements.define(
    'subscription-selector',
    class SubscriptionSelector extends HTMLElement {
      // Recharge purchaseOption enum values (from subscription-widget.js).
      static PURCHASE_ONETIME = 'onetime';

      connectedCallback() {
        // Capture phase → runs before ProductForm.handleFormSubmit (bubble phase on the form).
        this.addEventListener('submit', this.handleBeforeSubmit, { capture: true });
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.handleBeforeSubmit, { capture: true });
      }

      get form() {
        return this.querySelector('form[is="product-form"]');
      }

      // Our own input, identified by class — never a foreign (e.g. Recharge-injected) one.
      get input() {
        return (
          this.querySelector('input.product-selling-plan-id') ||
          this.querySelector('input[name="selling_plan"]')
        );
      }

      get productId() {
        return this.dataset.productId;
      }

      get isSubscriptionOnly() {
        return this.dataset.subscriptionOnly === 'true';
      }

      /** Currently applied selling plan id, or null for a one-time purchase. */
      get sellingPlan() {
        const value = this.input?.value;
        return value ? value : null;
      }

      /**
       * Free-gift descriptors emitted by Liquid (snippets/free-gift-data.liquid), or [].
       * Each: { variantId, sellingPlanId|null, quantity }. Empty when the hero has no gifts.
       */
      get freeGifts() {
        const optedOut = (window.__burstGiftOptOut || {})[this.dataset.productId];
        return [...this.querySelectorAll('.free-gift-item')]
          .filter((el) => !(optedOut && optedOut.has(String(el.dataset.variantId))))
          .map((el) => ({
            variantId: el.dataset.variantId,
            sellingPlanId: el.dataset.sellingPlanId || null,
            quantity: parseInt(el.dataset.quantity, 10) || 1,
            subscriptionCopy: el.dataset.subscriptionCopy || null,
          }))
          .filter((gift) => gift.variantId);
      }

      /**
       * Paid add-on line items pulled from the <product-addons> block (toggled + visible),
       * or []. Each: { id, quantity, selling_plan? }. These are plain, independent lines.
       */
      get addonItems() {
        // The <product-addons> block is a sibling of this wrapper (not a descendant), so match
        // by product id on the page. Scoped by product id so a related-products quick-view can't
        // leak. Shared by the main form AND the sticky ATC bar (same product id).
        const el =
          document.querySelector(`product-addons[data-product-id="${this.productId}"]`) ||
          (this.productId ? null : document.querySelector('product-addons'));
        return el && typeof el.getSelectedItems === 'function' ? el.getSelectedItems() : [];
      }

      /**
       * The Recharge widget for THIS product. Matched strictly by product-id — never a global
       * fallback, or a quick-view of another product (e.g. "Customers also bought") would grab the
       * main PDP's widget and apply the wrong product's selling plan.
       */
      get rechargeWidget() {
        return document.querySelector(`recharge-subscription-widget[product-id="${this.productId}"]`);
      }

      /** Extra replacement-head count for a one-time purchase (from a product metafield), or null. */
      get extraCrowns() {
        return this.dataset.extraCrowns || null;
      }

      /** Map of variant id → valid selling-plan ids for this product (from Liquid), or null. */
      get variantPlans() {
        if (this._variantPlans === undefined) {
          const el = this.querySelector('.subscription-variant-plans');
          try {
            this._variantPlans = el ? JSON.parse(el.textContent) : null;
          } catch (e) {
            this._variantPlans = null;
          }
        }
        return this._variantPlans;
      }

      /**
       * Apply a selling plan to the form.
       * @param {string|number|null} id - selling plan id, or null/'' for one-time.
       */
      setSellingPlan(id) {
        const input = this.input;
        if (!input) return;

        const value = id == null ? '' : String(id);
        if (input.value === value) return;

        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Read the Recharge widget's live selection and write it into the hidden input
       * before the form serializes. No-op when no widget exists (subscription-only is
       * already seeded server-side; one-time-only products have no selector at all).
       */
      handleBeforeSubmit = (event) => {
        // The Recharge widget injects its own empty `selling_plan` input into the nearby
        // product form at runtime. Left alone it serializes alongside ours and Shopify takes
        // the empty value (→ one-time). Disable any selling_plan input that isn't ours so only
        // our value reaches /cart/add. Runs every submit (idempotent).
        this.neutralizeForeignInputs();

        // Resolve the hero's selling plan for this submit.
        const widget = this.rechargeWidget;
        if (widget) {
          const planId = this.isSubscriptionPurchase(widget)
            ? widget.sellingPlanId || widget.defaultSellingPlanId || this.sellingPlan
            : null;
          this.setSellingPlan(planId || null);
        }
        // No widget → leave the server-seeded value (subscription-only already has its plan).

        // Ensure the resolved plan is valid for the SELECTED variant (prevents "Cannot apply
        // selling plan to variant" when the plan is stale after a variant change, or came from
        // another product's widget).
        this.validatePlanForVariant();

        // _purchase_type (+ _one_time / _one_time_extra_crowns) ride the plain single-item submit
        // path via these synced hidden inputs; addBundle() below stamps its own JSON items[] instead.
        const heroPlan = this.sellingPlan;
        const purchaseType = heroPlan ? 'subscription' : 'onetime';
        this.syncHiddenProperties(purchaseType);

        // Take over the submit when we need to add more than the hero in one /cart/add.js:
        //   - subscription hero WITH free gift(s) → bundle them (shared _bundle_id), OR
        //   - any toggled paid add-on(s) → append as plain, independent line items.
        // Building items[] explicitly means add-ons survive on one-time AND subscription
        // (there is no "keep only items[0]" strip step in this theme).
        const gifts = this.freeGifts;
        const addons = this.addonItems;
        if ((heroPlan && gifts.length) || addons.length) {
          event.preventDefault();
          event.stopImmediatePropagation(); // suppress ProductForm's single-item add
          this.addBundle(heroPlan, gifts, event.submitter, addons);
        }
      };

      /**
       * Keep the native (single-item) submit path's hidden properties[...] inputs in sync:
       * _purchase_type always; _one_time / _one_time_extra_crowns only for a one-time purchase
       * of a product carrying the extra-crowns metafield. Removed (not blanked) when they don't
       * apply, so a subscription submit never carries them at all.
       */
      syncHiddenProperties(purchaseType) {
        this.setHiddenProperty('_purchase_type', 'product-purchase-type', purchaseType);

        const extraCrowns = purchaseType === 'onetime' ? this.extraCrowns : null;
        this.setHiddenProperty('_one_time', 'product-one-time', extraCrowns ? 'true' : null);
        this.setHiddenProperty('_one_time_extra_crowns', 'product-one-time-extra-crowns', extraCrowns);

        // Subscription checkout copy: disabled (not removed) on one-time so it's excluded from
        // FormData but restored if the shopper switches back to subscribe.
        const subCopy = this.form?.querySelector('input.product-subscription-copy');
        if (subCopy) subCopy.disabled = purchaseType !== 'subscription';
      }

      /** Add/update/remove a hidden properties[key] input on the form, identified by a stable class. */
      setHiddenProperty(propertyKey, className, value) {
        const form = this.form;
        if (!form) return;
        let input = form.querySelector(`input.${className}`);
        if (value == null) {
          if (input) input.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${propertyKey}]`;
          input.className = className;
          form.appendChild(input);
        }
        input.value = value;
      }

      /**
       * Keep the selling plan valid for the currently-selected variant. If the plan isn't one of
       * the variant's allocations, remap to the variant's first plan; if the variant has no plans,
       * clear it (one-time). No-op when no variant map is present (e.g. cards).
       */
      validatePlanForVariant() {
        const plan = this.sellingPlan;
        if (!plan) return; // one-time — nothing to validate

        const map = this.variantPlans;
        const variantId = this.form?.querySelector('[name="id"]')?.value;
        if (!map || !variantId) return;

        const validPlans = map[String(variantId)] || [];
        if (validPlans.length === 0) {
          this.setSellingPlan(null); // selected variant has no subscription
        } else if (!validPlans.includes(String(plan))) {
          this.setSellingPlan(validPlans[0]); // remap to a plan valid for this variant
        }
      }

      /** Exclude any non-owned `selling_plan` inputs (e.g. Recharge-injected) from the payload. */
      neutralizeForeignInputs() {
        const ours = this.input;
        this.form?.querySelectorAll('input[name="selling_plan"]').forEach((el) => {
          if (el !== ours) el.disabled = true;
        });
      }

      /**
       * Add the hero + its free gift(s) in a single /cart/add.js, grouped by `_bundle_id`.
       * Hero is `main` (with its selling plan + _product_id); each gift is `individual` with
       * its own selling plan. Quantities scale with the hero quantity.
       *
       * `_bundle_id` is the **hero product id** (stable, not a per-add token): identical gift
       * lines across variants/repeat-adds therefore share variant+plan+properties and Shopify
       * MERGES them into one line, bumping quantity — so the same gift is never duplicated.
       *
       * Reuses the theme's `cart:refresh` path to re-render + open the drawer.
       */
      addBundle(heroPlan, gifts, submitter, addons = []) {
        const form = this.form;
        // `[name="id"]` is an <input> on the main/card forms but a <select> on the sticky bar.
        const heroVariantId = form?.querySelector('[name="id"]')?.value;
        if (!heroVariantId) return;

        const qtyInput = form?.querySelector('[name="quantity"]');
        const heroQty = qtyInput ? Math.max(parseInt(qtyInput.value, 10) || 1, 1) : 1;
        const bundleId = this.productId; // stable → Shopify merges identical gift lines
        const purchaseType = heroPlan ? 'subscription' : 'onetime';

        // One-time never carries gifts/add-ons — they're subscription perks (defensive: keeps this
        // true even if a toggle's hide-on-onetime state and the resolved plan briefly disagree).
        if (!heroPlan) {
          gifts = [];
          addons = [];
        }

        // Hero line. Bundle properties are only added when there ARE gifts to group under it;
        // otherwise (one-time, or subscription without gifts + add-ons) it's a plain line.
        const heroItem = { id: heroVariantId, quantity: heroQty, properties: { _purchase_type: purchaseType } };
        if (heroPlan) heroItem.selling_plan = heroPlan;
        if (gifts.length) {
          Object.assign(heroItem.properties, {
            _bundle_item: 'main',
            _bundle_id: bundleId,
            _product_id: this.productId,
            Includes: '',
          });
        }
        const heroSubCopy = form?.querySelector('.product-subscription-copy')?.value;
        if (heroPlan && heroSubCopy) heroItem.properties.Subscription = heroSubCopy;

        // Extra replacement heads: only the hero line, only a one-time purchase, only when the
        // product carries the metafield (this.extraCrowns). Never on subscription.
        if (purchaseType === 'onetime' && this.extraCrowns) {
          heroItem.properties._one_time = 'true';
          heroItem.properties._one_time_extra_crowns = this.extraCrowns;
        }

        const items = [
          heroItem,
          ...gifts.map((gift) => {
            const giftProps = {
              _bundle_item: 'individual',
              _bundle_id: bundleId,
              _hero_variant_id: heroVariantId,
              _purchase_type: purchaseType,
            };
            if (gift.subscriptionCopy) giftProps.Subscription = gift.subscriptionCopy;
            const item = {
              id: gift.variantId,
              quantity: heroQty * gift.quantity,
              properties: giftProps,
            };
            if (gift.sellingPlanId) item.selling_plan = gift.sellingPlanId;
            return item;
          }),
          // Paid add-ons: plain, independent line items (no bundle grouping) so they render
          // as standalone, editable cart lines. Tagged with the hero's product id via the
          // hidden `_addon_parent_id` property so cart-addon-reconciler.js can auto-remove the
          // add-on if its hero ever leaves the cart (no orphaned deals). It is NOT a
          // `_bundle_item`, so Recharge's bundle-integrity (422) rule never applies to it.
          ...addons.map((addon) => {
            const item = {
              id: addon.id,
              quantity: addon.quantity || 1,
              properties: {
                _addon_parent_id: this.productId,
                _hero_variant_id: heroVariantId,
                _purchase_type: purchaseType,
              },
            };
            if (addon.selling_plan) item.selling_plan = addon.selling_plan;
            return item;
          }),
        ];

        const button = submitter || form?.querySelector('[type="submit"]');
        button?.classList.add('btn--loading');
        button?.setAttribute('aria-disabled', 'true');

        fetch(`${FoxTheme.routes.cart_add_url}`, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items }),
        })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status) {
              this.showError(parsedState.description);
              return;
            }
            this.openCartAfterAdd();
          })
          .catch((error) => console.error('[free-gift] bundle add failed', error))
          .finally(() => {
            button?.classList.remove('btn--loading');
            button?.removeAttribute('aria-disabled');
          });
      }

      /**
       * Refresh the cart and open the drawer. When the add came from a quick-view modal, close the
       * modal first and open the drawer only after it has hidden (mirrors ProductForm.showCartDrawer)
       * — otherwise the cart drawer opens behind the still-open quick view.
       */
      openCartAfterAdd() {
        const quickView = this.closest('quick-view-modal');
        if (!quickView) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
          return;
        }

        // Refresh cart contents/count now; defer opening the drawer until the quick view closes.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        const drawer = document.querySelector('cart-drawer');
        if (drawer && !drawer.open) {
          document.body.addEventListener(
            quickView.events.handleAfterHide,
            () => {
              setTimeout(() => drawer.show());
            },
            { once: true }
          );
        }
        quickView.hide(true);
      }

      showError(message) {
        if (!message) return;
        const el = this.form?.querySelector('.product-form__error-message');
        if (el) {
          el.textContent = message;
          el.hidden = false;
        } else {
          console.error('[free-gift]', message);
        }
      }

      /**
       * True unless the widget is explicitly on the one-time option.
       * Covers 'subscription' and 'prepaid_subscription'; falls back to plan presence
       * if purchaseOption is not yet populated.
       */
      isSubscriptionPurchase(widget) {
        const option = widget.purchaseOption;
        if (option === SubscriptionSelector.PURCHASE_ONETIME) return false;
        if (typeof option === 'string' && option.length) return true;
        return Boolean(widget.sellingPlanId);
      }
    }
  );
}


// Free-gift opt-out toggles on the PDP ("Free products" block, "Let customers turn gifts off").
// Records unchecked gifts per hero product; <subscription-selector>.freeGifts skips them, so the
// main button, sticky bar and Quick View all add the same gifts.
(function () {
  if (window.__burstGiftToggleInit) return;
  window.__burstGiftToggleInit = true;
  window.__burstGiftOptOut = window.__burstGiftOptOut || {};
  document.addEventListener('change', (event) => {
    const toggle = event.target.closest && event.target.closest('[data-gift-toggle]');
    if (!toggle) return;
    const pid = String(toggle.dataset.productId);
    const vid = String(toggle.dataset.variantId);
    const set = (window.__burstGiftOptOut[pid] = window.__burstGiftOptOut[pid] || new Set());
    if (toggle.checked) set.delete(vid);
    else set.add(vid);
    // Keep every copy of this gift's toggle (e.g. PDP + Quick View) in sync, and dim the row when off.
    document.querySelectorAll(`[data-gift-toggle][data-product-id="${pid}"][data-variant-id="${vid}"]`).forEach((t) => {
      t.checked = toggle.checked;
      t.closest('.product__free-product')?.classList.toggle('is-opted-out', !toggle.checked);
    });
  });
})();
